// ============================================================
// Season 4 Format Pitch Data
// ============================================================

export interface PitchMatch {
  id: string;
  label: string;
  bo: string;
  time: string;
  day: string;
  section: 'upper' | 'lower' | 'final';
  round: number;
  slot: number;
  team1: string;
  team2: string;
}

export interface Connection {
  from: string;
  to: string;
}

export interface ScheduleItem {
  type: 'match' | 'break' | 'divider';
  label?: string;
  time?: string;
  icon?: string;
  ids?: string[];
}

export interface GroupSeed {
  seed: number;
  dest: string;
  color: 'upper' | 'lower';
  isBye?: boolean;
}

export interface GroupInfo {
  name: string;
  seeds: GroupSeed[];
}

export interface BracketPosition {
  id: string;
  top: number;
  left: number;
  section: 'UPPER' | 'LOWER';
}

export interface BracketConnector {
  start: { x: number; y: number };
  end: { x: number; y: number };
}

export interface BracketLabel {
  title: string;
  top: number;
  left: number;
}

export interface MobileSection {
  title: string;
  sectionClass: string;
  rounds: { label: string; ids: string[] }[];
}

export interface FlowDiagramLine {
  type: 'label' | 'line' | 'note';
  text: string;
  section?: 'upper' | 'lower';
}

export interface PitchConfig {
  id: number;
  name: string;
  description: string;
  teamCount: number;
  format: string;
  matches: PitchMatch[];
  winnerConnections: Connection[];
  loserConnections: Connection[];
  fridayBo1: ScheduleItem[];
  fridayBo3: ScheduleItem[];
  bracketSchedule: ScheduleItem[];
  groups: GroupInfo[];
  seedingDescription: string;
  hasGroupToggle?: boolean;
  // Bracket layout
  bracketPositions: BracketPosition[];
  bracketConnectors: BracketConnector[];
  bracketHeight: number;
  bracketRoundHeaders: BracketLabel[];
  bracketByeLabels?: BracketLabel[];
  bracketSectionDividerY?: number;
  showUpperLowerLabels?: boolean;
  mobileSections: MobileSection[];
  flowDiagram: FlowDiagramLine[];
}

// ============================================================
// Layout Constants for SVG Bracket
// ============================================================
export const BRACKET_LAYOUT = {
  BOX_W: 195,
  BOX_H: 92,
  COL_GAP: 55,
  UPPER_Y: 70,
  LOWER_Y: 430,
};

// ============================================================
// Pitch 1 — Double Elimination with Group Seeding
// ============================================================

const PITCH_1_MATCHES: PitchMatch[] = [
  // Group Stage — Friday (BO1 variants)
  { id: 'GR1', label: 'Round 1', bo: 'BO1', time: '20:00', day: 'Friday', section: 'upper', round: -1, slot: 0, team1: 'Group matches', team2: '(all pitches)' },
  { id: 'GR2', label: 'Round 2', bo: 'BO1', time: '21:00', day: 'Friday', section: 'upper', round: -1, slot: 1, team1: 'Group matches', team2: '(all pitches)' },
  { id: 'GR3', label: 'Round 3', bo: 'BO1', time: '22:00', day: 'Friday', section: 'upper', round: -1, slot: 2, team1: 'Group matches', team2: '(all pitches)' },
  // Group Stage — Friday (BO3 variants)
  { id: 'GR1B', label: 'Round 1', bo: 'BO3', time: '16:00', day: 'Friday', section: 'upper', round: -1, slot: 0, team1: 'Group matches', team2: '(all pitches)' },
  { id: 'GR2B', label: 'Round 2', bo: 'BO3', time: '19:00', day: 'Friday', section: 'upper', round: -1, slot: 1, team1: 'Group matches', team2: '(all pitches)' },
  { id: 'GR3B', label: 'Round 3', bo: 'BO3', time: '21:00', day: 'Friday', section: 'upper', round: -1, slot: 2, team1: 'Group matches', team2: '(all pitches)' },
  // Upper Bracket R1 — Seed 2 vs Seed 3 cross-group
  { id: 'K1', label: 'Match 1', bo: 'BO1', time: '11:00', day: 'Saturday', section: 'upper', round: 0, slot: 0, team1: 'G1 #2', team2: 'G2 #3' },
  { id: 'K2', label: 'Match 2', bo: 'BO1', time: '11:00', day: 'Saturday', section: 'upper', round: 0, slot: 1, team1: 'G2 #2', team2: 'G1 #3' },
  // Upper Bracket R2 — #1 seeds waiting for winners
  { id: 'UB1', label: 'UB 1', bo: 'BO3', time: '12:00', day: 'Saturday', section: 'upper', round: 1, slot: 0, team1: 'G2 #1', team2: 'Winner Match 1' },
  { id: 'UB2', label: 'UB 2', bo: 'BO3', time: '12:00', day: 'Saturday', section: 'upper', round: 1, slot: 1, team1: 'G1 #1', team2: 'Winner Match 2' },
  // Upper Bracket Final
  { id: 'UBF', label: 'UB Final', bo: 'BO3', time: '21:00', day: 'Saturday', section: 'upper', round: 2, slot: 0, team1: 'Winner UB 1', team2: 'Winner UB 2' },
  // Grand Final
  { id: 'GF', label: 'Grand Final', bo: 'BO3', time: '13:00', day: 'Sunday', section: 'final', round: 3, slot: 0, team1: 'Winner UB Final', team2: 'Winner LB Final' },
  // Lower Bracket R1 — #4 seeds vs losers of BO1s
  { id: 'LB1', label: 'LB 1', bo: 'BO3', time: '15:00', day: 'Saturday', section: 'lower', round: 0, slot: 0, team1: 'G2 #4', team2: 'Loser Match 1' },
  { id: 'LB2', label: 'LB 2', bo: 'BO3', time: '15:00', day: 'Saturday', section: 'lower', round: 0, slot: 1, team1: 'G1 #4', team2: 'Loser Match 2' },
  // Lower Bracket R2 — LB winners vs UB R2 losers
  { id: 'LB3', label: 'LB 3', bo: 'BO3', time: '18:00', day: 'Saturday', section: 'lower', round: 1, slot: 0, team1: 'Winner LB 1', team2: 'Loser UB 1' },
  { id: 'LB4', label: 'LB 4', bo: 'BO3', time: '18:00', day: 'Saturday', section: 'lower', round: 1, slot: 1, team1: 'Winner LB 2', team2: 'Loser UB 2' },
  // Lower Bracket Semi
  { id: 'LB5', label: 'LB 5', bo: 'BO3', time: '21:00', day: 'Saturday', section: 'lower', round: 2, slot: 0, team1: 'Winner LB 3', team2: 'Winner LB 4' },
  // Lower Bracket Final
  { id: 'LBF', label: 'LB Final', bo: 'BO3', time: '10:00', day: 'Sunday', section: 'lower', round: 3, slot: 0, team1: 'Winner LB 5', team2: 'Loser UB Final' },
];

const PITCH_1_WINNER_CONNECTIONS: Connection[] = [
  { from: 'K1', to: 'UB1' },
  { from: 'K2', to: 'UB2' },
  { from: 'UB1', to: 'UBF' },
  { from: 'UB2', to: 'UBF' },
  { from: 'UBF', to: 'GF' },
  { from: 'LB1', to: 'LB3' },
  { from: 'LB2', to: 'LB4' },
  { from: 'LB3', to: 'LB5' },
  { from: 'LB4', to: 'LB5' },
  { from: 'LB5', to: 'LBF' },
  { from: 'LBF', to: 'GF' },
];

const PITCH_1_LOSER_CONNECTIONS: Connection[] = [
  { from: 'K1', to: 'LB1' },
  { from: 'K2', to: 'LB2' },
  { from: 'UB1', to: 'LB3' },
  { from: 'UB2', to: 'LB4' },
  { from: 'UBF', to: 'LBF' },
];

const PITCH_1_FRIDAY_BO1: ScheduleItem[] = [
  { type: 'break', label: 'MEDIA DAY', time: '17:00', icon: 'coffee' },
  { type: 'break', label: 'DINNER', time: '18:00\u201319:30', icon: 'utensils' },
  { type: 'match', ids: ['GR1'] },
  { type: 'match', ids: ['GR2'] },
  { type: 'match', ids: ['GR3'] },
];

const PITCH_1_FRIDAY_BO3: ScheduleItem[] = [
  { type: 'break', label: 'MEDIA DAY', time: '13:00', icon: 'coffee' },
  { type: 'match', ids: ['GR1B'] },
  { type: 'break', label: 'DINNER', time: '18:00\u201318:45', icon: 'utensils' },
  { type: 'match', ids: ['GR2B'] },
  { type: 'match', ids: ['GR3B'] },
];

const PITCH_1_BRACKET_SCHEDULE: ScheduleItem[] = [
  { type: 'divider', label: 'SATURDAY' },
  { type: 'break', label: 'BREAKFAST', time: '08:00', icon: 'coffee' },
  { type: 'match', ids: ['K1', 'K2'] },
  { type: 'match', ids: ['UB1', 'UB2'] },
  { type: 'break', label: 'LUNCH', time: '14:00\u201315:00', icon: 'utensils' },
  { type: 'match', ids: ['LB1', 'LB2'] },
  { type: 'match', ids: ['LB3', 'LB4'] },
  { type: 'break', label: 'DINNER', time: '20:00\u201320:30', icon: 'bowl' },
  { type: 'match', ids: ['LB5', 'UBF'] },
  { type: 'divider', label: 'SUNDAY' },
  { type: 'break', label: 'BREAKFAST', time: '08:00\u201309:30', icon: 'coffee' },
  { type: 'match', ids: ['LBF'] },
  { type: 'match', ids: ['GF'] },
];

const PITCH_1_GROUPS: GroupInfo[] = [
  {
    name: 'Group 1',
    seeds: [
      { seed: 1, dest: '\u2192 UB 2 (bye, awaits Match 2 winner)', color: 'upper', isBye: true },
      { seed: 2, dest: '\u2192 Match 1 vs G2 #3 (BO1)', color: 'upper' },
      { seed: 3, dest: '\u2192 Match 2 vs G2 #2 (BO1)', color: 'upper' },
      { seed: 4, dest: '\u2192 LB 2 vs Loser Match 2 (BO3)', color: 'lower' },
    ],
  },
  {
    name: 'Group 2',
    seeds: [
      { seed: 1, dest: '\u2192 UB 1 (bye, awaits Match 1 winner)', color: 'upper', isBye: true },
      { seed: 2, dest: '\u2192 Match 2 vs G1 #3 (BO1)', color: 'upper' },
      { seed: 3, dest: '\u2192 Match 1 vs G1 #2 (BO1)', color: 'upper' },
      { seed: 4, dest: '\u2192 LB 1 vs Loser Match 1 (BO3)', color: 'lower' },
    ],
  },
];

export const PITCH_1: PitchConfig = {
  id: 1,
  name: 'Groups + Double Elim',
  description: '8 teams \u2022 Double Elimination \u2022 Friday\u2013Sunday',
  teamCount: 8,
  format: 'Double Elimination',
  matches: PITCH_1_MATCHES,
  winnerConnections: PITCH_1_WINNER_CONNECTIONS,
  loserConnections: PITCH_1_LOSER_CONNECTIONS,
  fridayBo1: PITCH_1_FRIDAY_BO1,
  fridayBo3: PITCH_1_FRIDAY_BO3,
  bracketSchedule: PITCH_1_BRACKET_SCHEDULE,
  groups: PITCH_1_GROUPS,
  seedingDescription: 'Top 2 from each group start in Upper Bracket (BO1). #1 seeds get a bye to round 2. #4 seeds start in Lower Bracket (BO3).',
  hasGroupToggle: true,
  bracketPositions: [
    { id: 'K1', top: 90, left: 20, section: 'UPPER' },
    { id: 'K2', top: 370, left: 20, section: 'UPPER' },
    { id: 'UB1', top: 90, left: 240, section: 'UPPER' },
    { id: 'UB2', top: 370, left: 240, section: 'UPPER' },
    { id: 'UBF', top: 220, left: 460, section: 'UPPER' },
    { id: 'GF', top: 220, left: 680, section: 'UPPER' },
    { id: 'LB1', top: 660, left: 20, section: 'LOWER' },
    { id: 'LB2', top: 800, left: 20, section: 'LOWER' },
    { id: 'LB3', top: 660, left: 240, section: 'LOWER' },
    { id: 'LB4', top: 800, left: 240, section: 'LOWER' },
    { id: 'LB5', top: 730, left: 460, section: 'LOWER' },
    { id: 'LBF', top: 730, left: 680, section: 'LOWER' },
  ],
  bracketConnectors: [
    { start: { x: 200, y: 162 }, end: { x: 240, y: 162 } },
    { start: { x: 200, y: 442 }, end: { x: 240, y: 442 } },
    { start: { x: 420, y: 162 }, end: { x: 460, y: 292 } },
    { start: { x: 420, y: 442 }, end: { x: 460, y: 292 } },
    { start: { x: 640, y: 292 }, end: { x: 680, y: 292 } },
    { start: { x: 200, y: 732 }, end: { x: 240, y: 732 } },
    { start: { x: 200, y: 872 }, end: { x: 240, y: 872 } },
    { start: { x: 420, y: 732 }, end: { x: 460, y: 802 } },
    { start: { x: 420, y: 872 }, end: { x: 460, y: 802 } },
    { start: { x: 640, y: 802 }, end: { x: 680, y: 802 } },
  ],
  bracketHeight: 945,
  bracketRoundHeaders: [
    { title: 'Upper Final', top: 180, left: 500 },
    { title: 'Grand Final', top: 180, left: 720 },
    { title: 'Lower Semi', top: 690, left: 490 },
    { title: 'Lower Final', top: 690, left: 710 },
  ],
  bracketByeLabels: [
    { title: 'G2 #1 BYE \u2192', top: 155, left: 190 },
    { title: 'G1 #1 BYE \u2192', top: 435, left: 190 },
  ],
  bracketSectionDividerY: 590,
  showUpperLowerLabels: true,
  mobileSections: [
    { title: 'UPPER BRACKET', sectionClass: 'upper', rounds: [
      { label: 'Round 1 (BO1)', ids: ['K1', 'K2'] },
      { label: 'Round 2 (BO3)', ids: ['UB1', 'UB2'] },
      { label: 'Upper Final', ids: ['UBF'] },
    ]},
    { title: 'LOWER BRACKET', sectionClass: 'lower', rounds: [
      { label: 'Round 1', ids: ['LB1', 'LB2'] },
      { label: 'Round 2', ids: ['LB3', 'LB4'] },
      { label: 'Lower Semi', ids: ['LB5'] },
      { label: 'Lower Final', ids: ['LBF'] },
    ]},
    { title: 'GRAND FINAL', sectionClass: 'finals', rounds: [
      { label: '', ids: ['GF'] },
    ]},
  ],
  flowDiagram: [
    { type: 'label', text: 'Upper Bracket:', section: 'upper' },
    { type: 'line', text: '  Match 1 (G1#2 vs G2#3) \u2500\u2500\u2192 UB 1 (vs G2#1) \u2500\u2500\u2510' },
    { type: 'line', text: '  Match 2 (G2#2 vs G1#3) \u2500\u2500\u2192 UB 2 (vs G1#1) \u2500\u2500\u2524\u2500\u2500\u2192 UB Final \u2500\u2500\u2192 GRAND FINAL' },
    { type: 'label', text: 'Lower Bracket:', section: 'lower' },
    { type: 'line', text: '  LB 1 (G2#4 vs Loser M1) \u2500\u2500\u2192 LB 3 (vs Loser UB1) \u2500\u2500\u2510' },
    { type: 'line', text: '  LB 2 (G1#4 vs Loser M2) \u2500\u2500\u2192 LB 4 (vs Loser UB2) \u2500\u2500\u2524\u2500\u2500\u2192 LB 5 \u2500\u2500\u2192 LB Final \u2500\u2500\u2192 GRAND FINAL' },
    { type: 'note', text: '  \u21B3 Loser of UB Final drops to LB Final' },
  ],
};

// ============================================================
// Pitch 2 — Groups + Double Elim (No BO1 — Direct Seed)
// ============================================================

const PITCH_2_MATCHES: PitchMatch[] = [
  // Group Stage — Friday (BO1 variants, same as Pitch 1)
  { id: 'GR1', label: 'Round 1', bo: 'BO1', time: '20:00', day: 'Friday', section: 'upper', round: -1, slot: 0, team1: 'Group matches', team2: '(all pitches)' },
  { id: 'GR2', label: 'Round 2', bo: 'BO1', time: '21:00', day: 'Friday', section: 'upper', round: -1, slot: 1, team1: 'Group matches', team2: '(all pitches)' },
  { id: 'GR3', label: 'Round 3', bo: 'BO1', time: '22:00', day: 'Friday', section: 'upper', round: -1, slot: 2, team1: 'Group matches', team2: '(all pitches)' },
  // Group Stage — Friday (BO3 variants)
  { id: 'GR1B', label: 'Round 1', bo: 'BO3', time: '16:00', day: 'Friday', section: 'upper', round: -1, slot: 0, team1: 'Group matches', team2: '(all pitches)' },
  { id: 'GR2B', label: 'Round 2', bo: 'BO3', time: '19:00', day: 'Friday', section: 'upper', round: -1, slot: 1, team1: 'Group matches', team2: '(all pitches)' },
  { id: 'GR3B', label: 'Round 3', bo: 'BO3', time: '21:00', day: 'Friday', section: 'upper', round: -1, slot: 2, team1: 'Group matches', team2: '(all pitches)' },
  // Upper Bracket — #1 vs cross-group #2 directly (no BO1 seeding)
  { id: 'UB1', label: 'UB 1', bo: 'BO3', time: '11:00', day: 'Saturday', section: 'upper', round: 0, slot: 0, team1: 'G1 #1', team2: 'G2 #2' },
  { id: 'UB2', label: 'UB 2', bo: 'BO3', time: '11:00', day: 'Saturday', section: 'upper', round: 0, slot: 1, team1: 'G2 #1', team2: 'G1 #2' },
  // Upper Bracket Final
  { id: 'UBF', label: 'UB Final', bo: 'BO3', time: '21:00', day: 'Saturday', section: 'upper', round: 1, slot: 0, team1: 'Winner UB 1', team2: 'Winner UB 2' },
  // Grand Final
  { id: 'GF', label: 'Grand Final', bo: 'BO3', time: '13:00', day: 'Sunday', section: 'final', round: 3, slot: 0, team1: 'Winner UB Final', team2: 'Winner LB Final' },
  // Lower Bracket R1 — #3 vs cross-group #4 directly
  { id: 'LB1', label: 'LB 1', bo: 'BO3', time: '15:00', day: 'Saturday', section: 'lower', round: 0, slot: 0, team1: 'G1 #3', team2: 'G2 #4' },
  { id: 'LB2', label: 'LB 2', bo: 'BO3', time: '15:00', day: 'Saturday', section: 'lower', round: 0, slot: 1, team1: 'G2 #3', team2: 'G1 #4' },
  // Lower Bracket R2 — LB winners vs UB losers
  { id: 'LB3', label: 'LB 3', bo: 'BO3', time: '18:00', day: 'Saturday', section: 'lower', round: 1, slot: 0, team1: 'Winner LB 1', team2: 'Loser UB 1' },
  { id: 'LB4', label: 'LB 4', bo: 'BO3', time: '18:00', day: 'Saturday', section: 'lower', round: 1, slot: 1, team1: 'Winner LB 2', team2: 'Loser UB 2' },
  // Lower Bracket Semi
  { id: 'LB5', label: 'LB 5', bo: 'BO3', time: '21:00', day: 'Saturday', section: 'lower', round: 2, slot: 0, team1: 'Winner LB 3', team2: 'Winner LB 4' },
  // Lower Bracket Final
  { id: 'LBF', label: 'LB Final', bo: 'BO3', time: '10:00', day: 'Sunday', section: 'lower', round: 3, slot: 0, team1: 'Winner LB 5', team2: 'Loser UB Final' },
];

const PITCH_2_FRIDAY_BO1: ScheduleItem[] = [
  { type: 'break', label: 'MEDIA DAY', time: '17:00', icon: 'coffee' },
  { type: 'break', label: 'DINNER', time: '18:00\u201319:30', icon: 'utensils' },
  { type: 'match', ids: ['GR1'] },
  { type: 'match', ids: ['GR2'] },
  { type: 'match', ids: ['GR3'] },
];

const PITCH_2_FRIDAY_BO3: ScheduleItem[] = [
  { type: 'break', label: 'MEDIA DAY', time: '13:00', icon: 'coffee' },
  { type: 'match', ids: ['GR1B'] },
  { type: 'break', label: 'DINNER', time: '18:00\u201318:45', icon: 'utensils' },
  { type: 'match', ids: ['GR2B'] },
  { type: 'match', ids: ['GR3B'] },
];

const PITCH_2_BRACKET_SCHEDULE: ScheduleItem[] = [
  { type: 'divider', label: 'SATURDAY' },
  { type: 'break', label: 'BREAKFAST', time: '08:00', icon: 'coffee' },
  { type: 'match', ids: ['UB1', 'UB2'] },
  { type: 'break', label: 'LUNCH', time: '14:00\u201315:00', icon: 'utensils' },
  { type: 'match', ids: ['LB1', 'LB2'] },
  { type: 'match', ids: ['LB3', 'LB4'] },
  { type: 'break', label: 'DINNER', time: '20:00\u201320:30', icon: 'bowl' },
  { type: 'match', ids: ['LB5', 'UBF'] },
  { type: 'divider', label: 'SUNDAY' },
  { type: 'break', label: 'BREAKFAST', time: '08:00\u201309:30', icon: 'coffee' },
  { type: 'match', ids: ['LBF'] },
  { type: 'match', ids: ['GF'] },
];

export const PITCH_2: PitchConfig = {
  id: 2,
  name: 'Groups + DE (No BO1)',
  description: '8 teams \u2022 Double Elimination \u2022 Friday\u2013Sunday',
  teamCount: 8,
  format: 'Double Elimination (Direct Seed)',
  matches: PITCH_2_MATCHES,
  winnerConnections: [
    { from: 'UB1', to: 'UBF' },
    { from: 'UB2', to: 'UBF' },
    { from: 'UBF', to: 'GF' },
    { from: 'LB1', to: 'LB3' },
    { from: 'LB2', to: 'LB4' },
    { from: 'LB3', to: 'LB5' },
    { from: 'LB4', to: 'LB5' },
    { from: 'LB5', to: 'LBF' },
    { from: 'LBF', to: 'GF' },
  ],
  loserConnections: [
    { from: 'UB1', to: 'LB3' },
    { from: 'UB2', to: 'LB4' },
    { from: 'UBF', to: 'LBF' },
  ],
  fridayBo1: PITCH_2_FRIDAY_BO1,
  fridayBo3: PITCH_2_FRIDAY_BO3,
  bracketSchedule: PITCH_2_BRACKET_SCHEDULE,
  hasGroupToggle: true,
  groups: [
    { name: 'Group 1', seeds: [
      { seed: 1, dest: '\u2192 UB 1 vs G2 #2 (BO3)', color: 'upper' },
      { seed: 2, dest: '\u2192 UB 2 vs G2 #1 (BO3)', color: 'upper' },
      { seed: 3, dest: '\u2192 LB 1 vs G2 #4 (BO3)', color: 'lower' },
      { seed: 4, dest: '\u2192 LB 2 vs G2 #3 (BO3)', color: 'lower' },
    ]},
    { name: 'Group 2', seeds: [
      { seed: 1, dest: '\u2192 UB 2 vs G1 #2 (BO3)', color: 'upper' },
      { seed: 2, dest: '\u2192 UB 1 vs G1 #1 (BO3)', color: 'upper' },
      { seed: 3, dest: '\u2192 LB 2 vs G1 #4 (BO3)', color: 'lower' },
      { seed: 4, dest: '\u2192 LB 1 vs G1 #3 (BO3)', color: 'lower' },
    ]},
  ],
  seedingDescription: 'Top 2 from each group go directly to Upper Bracket (BO3). Bottom 2 go directly to Lower Bracket (BO3). No BO1 seeding matches \u2014 all bracket matches are BO3.',
  bracketPositions: [
    { id: 'UB1', top: 90, left: 20, section: 'UPPER' },
    { id: 'UB2', top: 370, left: 20, section: 'UPPER' },
    { id: 'UBF', top: 220, left: 240, section: 'UPPER' },
    { id: 'GF', top: 220, left: 680, section: 'UPPER' },
    { id: 'LB1', top: 660, left: 20, section: 'LOWER' },
    { id: 'LB2', top: 800, left: 20, section: 'LOWER' },
    { id: 'LB3', top: 660, left: 240, section: 'LOWER' },
    { id: 'LB4', top: 800, left: 240, section: 'LOWER' },
    { id: 'LB5', top: 730, left: 460, section: 'LOWER' },
    { id: 'LBF', top: 730, left: 680, section: 'LOWER' },
  ],
  bracketConnectors: [
    // Upper: UB1/UB2 → UBF → GF
    { start: { x: 200, y: 162 }, end: { x: 240, y: 292 } },
    { start: { x: 200, y: 442 }, end: { x: 240, y: 292 } },
    { start: { x: 420, y: 292 }, end: { x: 680, y: 292 } },
    // Lower: LB1/LB2 → LB3/LB4 → LB5 → LBF
    { start: { x: 200, y: 732 }, end: { x: 240, y: 732 } },
    { start: { x: 200, y: 872 }, end: { x: 240, y: 872 } },
    { start: { x: 420, y: 732 }, end: { x: 460, y: 802 } },
    { start: { x: 420, y: 872 }, end: { x: 460, y: 802 } },
    { start: { x: 640, y: 802 }, end: { x: 680, y: 802 } },
  ],
  bracketHeight: 945,
  bracketRoundHeaders: [
    { title: 'Upper Final', top: 180, left: 280 },
    { title: 'Grand Final', top: 180, left: 720 },
    { title: 'Lower Semi', top: 690, left: 490 },
    { title: 'Lower Final', top: 690, left: 710 },
  ],
  bracketSectionDividerY: 590,
  showUpperLowerLabels: true,
  mobileSections: [
    { title: 'UPPER BRACKET', sectionClass: 'upper', rounds: [
      { label: 'Round 1 (BO3)', ids: ['UB1', 'UB2'] },
      { label: 'Upper Final', ids: ['UBF'] },
    ]},
    { title: 'LOWER BRACKET', sectionClass: 'lower', rounds: [
      { label: 'Round 1', ids: ['LB1', 'LB2'] },
      { label: 'Round 2', ids: ['LB3', 'LB4'] },
      { label: 'Lower Semi', ids: ['LB5'] },
      { label: 'Lower Final', ids: ['LBF'] },
    ]},
    { title: 'GRAND FINAL', sectionClass: 'finals', rounds: [
      { label: '', ids: ['GF'] },
    ]},
  ],
  flowDiagram: [
    { type: 'label', text: 'Upper Bracket:', section: 'upper' },
    { type: 'line', text: '  UB 1 (G1#1 vs G2#2) \u2500\u2500\u2510' },
    { type: 'line', text: '  UB 2 (G2#1 vs G1#2) \u2500\u2500\u2524\u2500\u2500\u2192 UB Final \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2192 GRAND FINAL' },
    { type: 'label', text: 'Lower Bracket:', section: 'lower' },
    { type: 'line', text: '  LB 1 (G1#3 vs G2#4) \u2500\u2500\u2192 LB 3 (vs Loser UB1) \u2500\u2500\u2510' },
    { type: 'line', text: '  LB 2 (G2#3 vs G1#4) \u2500\u2500\u2192 LB 4 (vs Loser UB2) \u2500\u2500\u2524\u2500\u2500\u2192 LB 5 \u2500\u2500\u2192 LB Final \u2500\u2500\u2192 GRAND FINAL' },
    { type: 'note', text: '  \u21B3 Loser of UB Final drops to LB Final' },
    { type: 'note', text: '  \u21B3 No BO1 matches \u2014 all bracket games are BO3' },
  ],
};

// ============================================================
// Pitch 3 — GSL Groups + Single Elim Playoffs
// ============================================================

const PITCH_3_MATCHES: PitchMatch[] = [
  // Group A — BO1 variants
  { id: 'GAO1', label: 'A Opening 1', bo: 'BO1', time: '20:00', day: 'Friday', section: 'upper', round: -1, slot: 0, team1: 'A1', team2: 'A2' },
  { id: 'GAO2', label: 'A Opening 2', bo: 'BO1', time: '20:00', day: 'Friday', section: 'upper', round: -1, slot: 1, team1: 'A3', team2: 'A4' },
  { id: 'GAW', label: 'A Winners', bo: 'BO3', time: '11:00', day: 'Saturday', section: 'upper', round: 0, slot: 0, team1: 'Winner A-O1', team2: 'Winner A-O2' },
  { id: 'GAL', label: 'A Losers', bo: 'BO3', time: '11:00', day: 'Saturday', section: 'lower', round: 0, slot: 1, team1: 'Loser A-O1', team2: 'Loser A-O2' },
  { id: 'GAD', label: 'A Decider', bo: 'BO3', time: '14:00', day: 'Saturday', section: 'upper', round: 1, slot: 0, team1: 'Loser A-Win', team2: 'Winner A-Los' },
  // Group B — BO1 variants
  { id: 'GBO1', label: 'B Opening 1', bo: 'BO1', time: '20:00', day: 'Friday', section: 'upper', round: -1, slot: 2, team1: 'B1', team2: 'B2' },
  { id: 'GBO2', label: 'B Opening 2', bo: 'BO1', time: '20:00', day: 'Friday', section: 'upper', round: -1, slot: 3, team1: 'B3', team2: 'B4' },
  { id: 'GBW', label: 'B Winners', bo: 'BO3', time: '11:00', day: 'Saturday', section: 'upper', round: 0, slot: 2, team1: 'Winner B-O1', team2: 'Winner B-O2' },
  { id: 'GBL', label: 'B Losers', bo: 'BO3', time: '11:00', day: 'Saturday', section: 'lower', round: 0, slot: 3, team1: 'Loser B-O1', team2: 'Loser B-O2' },
  { id: 'GBD', label: 'B Decider', bo: 'BO3', time: '14:00', day: 'Saturday', section: 'upper', round: 1, slot: 1, team1: 'Loser B-Win', team2: 'Winner B-Los' },
  // BO3 opening variants
  { id: 'GAO1B', label: 'A Opening 1', bo: 'BO3', time: '16:00', day: 'Friday', section: 'upper', round: -1, slot: 0, team1: 'A1', team2: 'A2' },
  { id: 'GAO2B', label: 'A Opening 2', bo: 'BO3', time: '16:00', day: 'Friday', section: 'upper', round: -1, slot: 1, team1: 'A3', team2: 'A4' },
  { id: 'GBO1B', label: 'B Opening 1', bo: 'BO3', time: '16:00', day: 'Friday', section: 'upper', round: -1, slot: 2, team1: 'B1', team2: 'B2' },
  { id: 'GBO2B', label: 'B Opening 2', bo: 'BO3', time: '16:00', day: 'Friday', section: 'upper', round: -1, slot: 3, team1: 'B3', team2: 'B4' },
  // Playoffs
  { id: 'SF1', label: 'Semifinal 1', bo: 'BO3', time: '17:00', day: 'Saturday', section: 'upper', round: 2, slot: 0, team1: 'A #1', team2: 'B #2' },
  { id: 'SF2', label: 'Semifinal 2', bo: 'BO3', time: '17:00', day: 'Saturday', section: 'upper', round: 2, slot: 1, team1: 'B #1', team2: 'A #2' },
  { id: 'FIN', label: 'Grand Final', bo: 'BO3', time: '10:00', day: 'Sunday', section: 'final', round: 3, slot: 0, team1: 'Winner SF1', team2: 'Winner SF2' },
];

const PITCH_3_FRIDAY_BO1: ScheduleItem[] = [
  { type: 'break', label: 'MEDIA DAY', time: '17:00', icon: 'coffee' },
  { type: 'break', label: 'DINNER', time: '18:00\u201319:30', icon: 'utensils' },
  { type: 'match', ids: ['GAO1', 'GAO2', 'GBO1', 'GBO2'] },
];

const PITCH_3_FRIDAY_BO3: ScheduleItem[] = [
  { type: 'break', label: 'MEDIA DAY', time: '13:00', icon: 'coffee' },
  { type: 'match', ids: ['GAO1B', 'GAO2B', 'GBO1B', 'GBO2B'] },
  { type: 'break', label: 'DINNER', time: '18:00\u201318:45', icon: 'utensils' },
];

const PITCH_3_BRACKET_SCHEDULE: ScheduleItem[] = [
  { type: 'divider', label: 'SATURDAY' },
  { type: 'break', label: 'BREAKFAST', time: '08:00', icon: 'coffee' },
  { type: 'match', ids: ['GAW', 'GAL', 'GBW', 'GBL'] },
  { type: 'break', label: 'LUNCH', time: '12:30\u201313:30', icon: 'utensils' },
  { type: 'match', ids: ['GAD', 'GBD'] },
  { type: 'match', ids: ['SF1', 'SF2'] },
  { type: 'divider', label: 'SUNDAY' },
  { type: 'break', label: 'BREAKFAST', time: '08:00\u201309:30', icon: 'coffee' },
  { type: 'match', ids: ['FIN'] },
];

export const PITCH_3: PitchConfig = {
  id: 3,
  name: 'GSL Groups + Playoffs',
  description: '8 teams \u2022 GSL Groups into Single Elim \u2022 Friday\u2013Sunday',
  teamCount: 8,
  format: 'GSL Groups + Single Elimination',
  matches: PITCH_3_MATCHES,
  winnerConnections: [],
  loserConnections: [],
  fridayBo1: PITCH_3_FRIDAY_BO1,
  fridayBo3: PITCH_3_FRIDAY_BO3,
  bracketSchedule: PITCH_3_BRACKET_SCHEDULE,
  hasGroupToggle: true,
  groups: [
    { name: 'Group A (GSL)', seeds: [
      { seed: 1, dest: '\u2192 A Opening 1', color: 'upper' },
      { seed: 2, dest: '\u2192 A Opening 1', color: 'upper' },
      { seed: 3, dest: '\u2192 A Opening 2', color: 'upper' },
      { seed: 4, dest: '\u2192 A Opening 2', color: 'upper' },
    ]},
    { name: 'Group B (GSL)', seeds: [
      { seed: 1, dest: '\u2192 B Opening 1', color: 'upper' },
      { seed: 2, dest: '\u2192 B Opening 1', color: 'upper' },
      { seed: 3, dest: '\u2192 B Opening 2', color: 'upper' },
      { seed: 4, dest: '\u2192 B Opening 2', color: 'upper' },
    ]},
  ],
  seedingDescription: 'GSL format: 2 groups of 4 play double-elimination mini-brackets. Winners of opening matches face each other (Winners match). Losers face each other (Losers match). The Losers match loser is eliminated. Winners match loser and Losers match winner play a Decider. Top 2 per group advance to cross-group semifinals.',
  bracketPositions: [
    // Group A
    { id: 'GAO1', top: 50, left: 20, section: 'UPPER' },
    { id: 'GAO2', top: 190, left: 20, section: 'UPPER' },
    { id: 'GAW', top: 50, left: 240, section: 'UPPER' },
    { id: 'GAL', top: 190, left: 240, section: 'UPPER' },
    { id: 'GAD', top: 120, left: 460, section: 'UPPER' },
    // Group B
    { id: 'GBO1', top: 400, left: 20, section: 'LOWER' },
    { id: 'GBO2', top: 540, left: 20, section: 'LOWER' },
    { id: 'GBW', top: 400, left: 240, section: 'LOWER' },
    { id: 'GBL', top: 540, left: 240, section: 'LOWER' },
    { id: 'GBD', top: 470, left: 460, section: 'LOWER' },
    // Playoffs
    { id: 'SF1', top: 90, left: 680, section: 'UPPER' },
    { id: 'SF2', top: 470, left: 680, section: 'UPPER' },
    { id: 'FIN', top: 280, left: 900, section: 'UPPER' },
  ],
  bracketConnectors: [
    // Group A: O1/O2 → Win, Win/Los → Dec
    { start: { x: 200, y: 122 }, end: { x: 240, y: 122 } },
    { start: { x: 200, y: 262 }, end: { x: 240, y: 122 } },
    { start: { x: 420, y: 122 }, end: { x: 460, y: 192 } },
    { start: { x: 420, y: 262 }, end: { x: 460, y: 192 } },
    { start: { x: 640, y: 192 }, end: { x: 680, y: 162 } },
    // Group B: O1/O2 → Win, Win/Los → Dec
    { start: { x: 200, y: 472 }, end: { x: 240, y: 472 } },
    { start: { x: 200, y: 612 }, end: { x: 240, y: 472 } },
    { start: { x: 420, y: 472 }, end: { x: 460, y: 542 } },
    { start: { x: 420, y: 612 }, end: { x: 460, y: 542 } },
    { start: { x: 640, y: 542 }, end: { x: 680, y: 542 } },
    // Playoffs: SF → Final
    { start: { x: 860, y: 162 }, end: { x: 900, y: 352 } },
    { start: { x: 860, y: 542 }, end: { x: 900, y: 352 } },
  ],
  bracketHeight: 700,
  bracketRoundHeaders: [
    { title: 'Opening', top: 15, left: 60 },
    { title: 'Win / Loss', top: 15, left: 275 },
    { title: 'Decider', top: 85, left: 500 },
    { title: 'Semifinal', top: 55, left: 720 },
    { title: 'Final', top: 245, left: 950 },
  ],
  bracketByeLabels: [
    { title: 'A#1 \u2192', top: 155, left: 630 },
    { title: 'B#1 \u2192', top: 535, left: 630 },
  ],
  bracketSectionDividerY: 345,
  showUpperLowerLabels: false,
  mobileSections: [
    { title: 'GROUP A (GSL)', sectionClass: 'upper', rounds: [
      { label: 'Opening', ids: ['GAO1', 'GAO2'] },
      { label: 'Winners / Losers', ids: ['GAW', 'GAL'] },
      { label: 'Decider', ids: ['GAD'] },
    ]},
    { title: 'GROUP B (GSL)', sectionClass: 'lower', rounds: [
      { label: 'Opening', ids: ['GBO1', 'GBO2'] },
      { label: 'Winners / Losers', ids: ['GBW', 'GBL'] },
      { label: 'Decider', ids: ['GBD'] },
    ]},
    { title: 'PLAYOFFS', sectionClass: 'finals', rounds: [
      { label: 'Semifinals', ids: ['SF1', 'SF2'] },
      { label: 'Grand Final', ids: ['FIN'] },
    ]},
  ],
  flowDiagram: [
    { type: 'label', text: 'GSL Group (per group):', section: 'upper' },
    { type: 'line', text: '  Opening 1 \u2500\u2500\u2510        Winners Match \u2500\u2500\u2192 #1 Qualified' },
    { type: 'line', text: '  Opening 2 \u2500\u2500\u2518 W          \u2502' },
    { type: 'line', text: '               L\u2500\u2500 Losers Match    Decider \u2500\u2500\u2192 #2 Qualified' },
    { type: 'note', text: '  \u21B3 Losers match loser is eliminated' },
    { type: 'label', text: 'Playoffs:', section: 'upper' },
    { type: 'line', text: '  SF1 (A#1 vs B#2) \u2500\u2500\u2510' },
    { type: 'line', text: '  SF2 (B#1 vs A#2) \u2500\u2500\u2524\u2500\u2500\u2192 Grand Final' },
  ],
};

// ============================================================
// Pitch 4 — Full Double Elimination (no groups)
// ============================================================

const PITCH_4_MATCHES: PitchMatch[] = [
  // Upper bracket QF
  { id: 'UBQ1', label: 'UB QF 1', bo: 'BO3', time: '11:00', day: 'Saturday', section: 'upper', round: 0, slot: 0, team1: 'Seed #1', team2: 'Seed #8' },
  { id: 'UBQ2', label: 'UB QF 2', bo: 'BO3', time: '11:00', day: 'Saturday', section: 'upper', round: 0, slot: 1, team1: 'Seed #4', team2: 'Seed #5' },
  { id: 'UBQ3', label: 'UB QF 3', bo: 'BO3', time: '14:00', day: 'Saturday', section: 'upper', round: 0, slot: 2, team1: 'Seed #2', team2: 'Seed #7' },
  { id: 'UBQ4', label: 'UB QF 4', bo: 'BO3', time: '14:00', day: 'Saturday', section: 'upper', round: 0, slot: 3, team1: 'Seed #3', team2: 'Seed #6' },
  // Upper bracket SF
  { id: 'UBS1', label: 'UB SF 1', bo: 'BO3', time: '17:00', day: 'Saturday', section: 'upper', round: 1, slot: 0, team1: 'Winner QF1', team2: 'Winner QF2' },
  { id: 'UBS2', label: 'UB SF 2', bo: 'BO3', time: '17:00', day: 'Saturday', section: 'upper', round: 1, slot: 1, team1: 'Winner QF3', team2: 'Winner QF4' },
  // Upper bracket final
  { id: 'UBF', label: 'UB Final', bo: 'BO3', time: '13:00', day: 'Sunday', section: 'upper', round: 2, slot: 0, team1: 'Winner SF1', team2: 'Winner SF2' },
  // Lower bracket R1
  { id: 'LR1A', label: 'LB R1-1', bo: 'BO3', time: '20:00', day: 'Saturday', section: 'lower', round: 0, slot: 0, team1: 'Loser QF1', team2: 'Loser QF4' },
  { id: 'LR1B', label: 'LB R1-2', bo: 'BO3', time: '20:00', day: 'Saturday', section: 'lower', round: 0, slot: 1, team1: 'Loser QF2', team2: 'Loser QF3' },
  // Lower bracket R2
  { id: 'LR2A', label: 'LB R2-1', bo: 'BO3', time: '10:00', day: 'Sunday', section: 'lower', round: 1, slot: 0, team1: 'Winner LR1-1', team2: 'Loser UB SF2' },
  { id: 'LR2B', label: 'LB R2-2', bo: 'BO3', time: '10:00', day: 'Sunday', section: 'lower', round: 1, slot: 1, team1: 'Winner LR1-2', team2: 'Loser UB SF1' },
  // Lower bracket semi + final
  { id: 'LBSF', label: 'LB Semi', bo: 'BO3', time: '15:00', day: 'Sunday', section: 'lower', round: 2, slot: 0, team1: 'Winner LR2-1', team2: 'Winner LR2-2' },
  { id: 'LBF', label: 'LB Final', bo: 'BO3', time: '17:00', day: 'Sunday', section: 'lower', round: 3, slot: 0, team1: 'Winner LB Semi', team2: 'Loser UB Final' },
  // Grand Final
  { id: 'GF', label: 'Grand Final', bo: 'BO3', time: '19:00', day: 'Sunday', section: 'final', round: 4, slot: 0, team1: 'Winner UB Final', team2: 'Winner LB Final' },
];

const PITCH_4_BRACKET_SCHEDULE: ScheduleItem[] = [
  { type: 'divider', label: 'SATURDAY' },
  { type: 'break', label: 'BREAKFAST', time: '08:00', icon: 'coffee' },
  { type: 'match', ids: ['UBQ1', 'UBQ2'] },
  { type: 'match', ids: ['UBQ3', 'UBQ4'] },
  { type: 'break', label: 'LUNCH', time: '15:30\u201316:30', icon: 'utensils' },
  { type: 'match', ids: ['UBS1', 'UBS2'] },
  { type: 'break', label: 'DINNER', time: '19:00\u201319:30', icon: 'bowl' },
  { type: 'match', ids: ['LR1A', 'LR1B'] },
  { type: 'divider', label: 'SUNDAY' },
  { type: 'break', label: 'BREAKFAST', time: '08:00\u201309:30', icon: 'coffee' },
  { type: 'match', ids: ['LR2A', 'LR2B'] },
  { type: 'match', ids: ['UBF'] },
  { type: 'match', ids: ['LBSF'] },
  { type: 'match', ids: ['LBF'] },
  { type: 'match', ids: ['GF'] },
];

export const PITCH_4: PitchConfig = {
  id: 4,
  name: 'Full Double Elim',
  description: '8 teams \u2022 Full Double Elimination \u2022 Saturday\u2013Sunday',
  teamCount: 8,
  format: 'Double Elimination',
  matches: PITCH_4_MATCHES,
  winnerConnections: [],
  loserConnections: [],
  fridayBo1: [{ type: 'break', label: 'MEDIA DAY', time: '17:00', icon: 'coffee' }],
  fridayBo3: [{ type: 'break', label: 'MEDIA DAY', time: '17:00', icon: 'coffee' }],
  bracketSchedule: PITCH_4_BRACKET_SCHEDULE,
  groups: [
    { name: 'Seeding (1\u20138)', seeds: [
      { seed: 1, dest: '\u2192 UB QF1 vs #8', color: 'upper' },
      { seed: 2, dest: '\u2192 UB QF3 vs #7', color: 'upper' },
      { seed: 3, dest: '\u2192 UB QF4 vs #6', color: 'upper' },
      { seed: 4, dest: '\u2192 UB QF2 vs #5', color: 'upper' },
    ]},
    { name: 'Seeding (5\u20138)', seeds: [
      { seed: 5, dest: '\u2192 UB QF2 vs #4', color: 'lower' },
      { seed: 6, dest: '\u2192 UB QF4 vs #3', color: 'lower' },
      { seed: 7, dest: '\u2192 UB QF3 vs #2', color: 'lower' },
      { seed: 8, dest: '\u2192 UB QF1 vs #1', color: 'lower' },
    ]},
  ],
  seedingDescription: 'All 8 teams enter the double elimination bracket directly. No group stage. Seeds are based on prior rankings. Every team is guaranteed at least 2 matches.',
  bracketPositions: [
    { id: 'UBQ1', top: 30, left: 20, section: 'UPPER' },
    { id: 'UBQ2', top: 170, left: 20, section: 'UPPER' },
    { id: 'UBQ3', top: 310, left: 20, section: 'UPPER' },
    { id: 'UBQ4', top: 450, left: 20, section: 'UPPER' },
    { id: 'UBS1', top: 95, left: 240, section: 'UPPER' },
    { id: 'UBS2', top: 375, left: 240, section: 'UPPER' },
    { id: 'UBF', top: 230, left: 460, section: 'UPPER' },
    { id: 'GF', top: 230, left: 680, section: 'UPPER' },
    { id: 'LR1A', top: 640, left: 20, section: 'LOWER' },
    { id: 'LR1B', top: 780, left: 20, section: 'LOWER' },
    { id: 'LR2A', top: 640, left: 240, section: 'LOWER' },
    { id: 'LR2B', top: 780, left: 240, section: 'LOWER' },
    { id: 'LBSF', top: 710, left: 460, section: 'LOWER' },
    { id: 'LBF', top: 710, left: 680, section: 'LOWER' },
  ],
  bracketConnectors: [
    // Upper bracket
    { start: { x: 200, y: 102 }, end: { x: 240, y: 167 } },
    { start: { x: 200, y: 242 }, end: { x: 240, y: 167 } },
    { start: { x: 200, y: 382 }, end: { x: 240, y: 447 } },
    { start: { x: 200, y: 522 }, end: { x: 240, y: 447 } },
    { start: { x: 420, y: 167 }, end: { x: 460, y: 302 } },
    { start: { x: 420, y: 447 }, end: { x: 460, y: 302 } },
    { start: { x: 640, y: 302 }, end: { x: 680, y: 302 } },
    // Lower bracket
    { start: { x: 200, y: 712 }, end: { x: 240, y: 712 } },
    { start: { x: 200, y: 852 }, end: { x: 240, y: 852 } },
    { start: { x: 420, y: 712 }, end: { x: 460, y: 782 } },
    { start: { x: 420, y: 852 }, end: { x: 460, y: 782 } },
    { start: { x: 640, y: 782 }, end: { x: 680, y: 782 } },
  ],
  bracketHeight: 920,
  bracketRoundHeaders: [
    { title: 'Quarterfinals', top: 0, left: 50 },
    { title: 'Semifinals', top: 60, left: 280 },
    { title: 'UB Final', top: 195, left: 500 },
    { title: 'Grand Final', top: 195, left: 720 },
    { title: 'LB Round 1', top: 610, left: 40 },
    { title: 'LB Round 2', top: 610, left: 265 },
    { title: 'LB Semi', top: 680, left: 500 },
    { title: 'LB Final', top: 680, left: 720 },
  ],
  bracketSectionDividerY: 575,
  showUpperLowerLabels: true,
  mobileSections: [
    { title: 'UPPER BRACKET', sectionClass: 'upper', rounds: [
      { label: 'Quarterfinals', ids: ['UBQ1', 'UBQ2', 'UBQ3', 'UBQ4'] },
      { label: 'Semifinals', ids: ['UBS1', 'UBS2'] },
      { label: 'UB Final', ids: ['UBF'] },
    ]},
    { title: 'LOWER BRACKET', sectionClass: 'lower', rounds: [
      { label: 'Round 1', ids: ['LR1A', 'LR1B'] },
      { label: 'Round 2', ids: ['LR2A', 'LR2B'] },
      { label: 'LB Semi', ids: ['LBSF'] },
      { label: 'LB Final', ids: ['LBF'] },
    ]},
    { title: 'GRAND FINAL', sectionClass: 'finals', rounds: [
      { label: '', ids: ['GF'] },
    ]},
  ],
  flowDiagram: [
    { type: 'label', text: 'Upper Bracket:', section: 'upper' },
    { type: 'line', text: '  QF1 (#1v#8) \u2500\u2510              UB Final \u2500\u2500\u2192 Grand Final' },
    { type: 'line', text: '  QF2 (#4v#5) \u2500\u2524\u2500 UB SF1 \u2500\u2510       \u2502' },
    { type: 'line', text: '  QF3 (#2v#7) \u2500\u2510          \u251C\u2500\u2500 UBF \u2500\u2518' },
    { type: 'line', text: '  QF4 (#3v#6) \u2500\u2524\u2500 UB SF2 \u2500\u2518' },
    { type: 'label', text: 'Lower Bracket:', section: 'lower' },
    { type: 'line', text: '  LR1 (QF losers) \u2500\u2192 LR2 (vs SF losers) \u2500\u2192 LB Semi \u2500\u2192 LB Final \u2500\u2192 GF' },
    { type: 'note', text: '  \u21B3 Every team guaranteed at least 2 matches' },
  ],
};

// ============================================================
// Pitch 5 — 10 Teams: Groups + Single Elim (Bottom 5 Eliminated)
// ============================================================

const PITCH_5_MATCHES: PitchMatch[] = [
  // Group Stage — Friday (BO1, 5 rounds for full round-robin in 2 groups of 5)
  { id: 'GR1', label: 'Round 1', bo: 'BO1', time: '17:00', day: 'Friday', section: 'upper', round: -1, slot: 0, team1: '4 group matches', team2: '' },
  { id: 'GR2', label: 'Round 2', bo: 'BO1', time: '18:00', day: 'Friday', section: 'upper', round: -1, slot: 1, team1: '4 group matches', team2: '' },
  { id: 'GR3', label: 'Round 3', bo: 'BO1', time: '19:00', day: 'Friday', section: 'upper', round: -1, slot: 2, team1: '4 group matches', team2: '' },
  { id: 'GR4', label: 'Round 4', bo: 'BO1', time: '20:00', day: 'Friday', section: 'upper', round: -1, slot: 3, team1: '4 group matches', team2: '' },
  { id: 'GR5', label: 'Round 5', bo: 'BO1', time: '21:00', day: 'Friday', section: 'upper', round: -1, slot: 4, team1: '4 group matches', team2: '' },
  // Group Stage — Friday (BO3, 3 rounds — partial round-robin)
  { id: 'GR1B', label: 'Round 1', bo: 'BO3', time: '14:00', day: 'Friday', section: 'upper', round: -1, slot: 0, team1: '4 group matches', team2: '' },
  { id: 'GR2B', label: 'Round 2', bo: 'BO3', time: '16:30', day: 'Friday', section: 'upper', round: -1, slot: 1, team1: '4 group matches', team2: '' },
  { id: 'GR3B', label: 'Round 3', bo: 'BO3', time: '19:30', day: 'Friday', section: 'upper', round: -1, slot: 2, team1: '4 group matches', team2: '' },
  // Bracket — Quarterfinal (5 teams, #1 gets bye)
  { id: 'QF', label: 'Quarterfinal', bo: 'BO3', time: '11:00', day: 'Saturday', section: 'upper', round: 0, slot: 0, team1: '#4 Seed', team2: '#5 Seed' },
  // Semifinals
  { id: 'SF1', label: 'Semifinal 1', bo: 'BO3', time: '14:00', day: 'Saturday', section: 'upper', round: 1, slot: 0, team1: '#1 Seed', team2: 'Winner QF' },
  { id: 'SF2', label: 'Semifinal 2', bo: 'BO3', time: '17:00', day: 'Saturday', section: 'upper', round: 1, slot: 1, team1: '#2 Seed', team2: '#3 Seed' },
  // 3rd Place
  { id: '3RD', label: '3rd Place', bo: 'BO3', time: '10:00', day: 'Sunday', section: 'lower', round: 2, slot: 0, team1: 'Loser SF1', team2: 'Loser SF2' },
  // Grand Final
  { id: 'FIN', label: 'Grand Final', bo: 'BO3', time: '13:00', day: 'Sunday', section: 'final', round: 2, slot: 1, team1: 'Winner SF1', team2: 'Winner SF2' },
];

const PITCH_5_FRIDAY_BO1: ScheduleItem[] = [
  { type: 'break', label: 'MEDIA DAY', time: '14:00', icon: 'coffee' },
  { type: 'break', label: 'DINNER', time: '16:00\u201317:00', icon: 'utensils' },
  { type: 'match', ids: ['GR1'] },
  { type: 'match', ids: ['GR2'] },
  { type: 'match', ids: ['GR3'] },
  { type: 'match', ids: ['GR4'] },
  { type: 'match', ids: ['GR5'] },
];

const PITCH_5_FRIDAY_BO3: ScheduleItem[] = [
  { type: 'break', label: 'MEDIA DAY', time: '12:00', icon: 'coffee' },
  { type: 'match', ids: ['GR1B'] },
  { type: 'match', ids: ['GR2B'] },
  { type: 'break', label: 'DINNER', time: '18:30\u201319:15', icon: 'utensils' },
  { type: 'match', ids: ['GR3B'] },
];

const PITCH_5_BRACKET_SCHEDULE: ScheduleItem[] = [
  { type: 'divider', label: 'SATURDAY' },
  { type: 'break', label: 'BREAKFAST', time: '08:00', icon: 'coffee' },
  { type: 'match', ids: ['QF'] },
  { type: 'break', label: 'LUNCH', time: '12:30\u201313:30', icon: 'utensils' },
  { type: 'match', ids: ['SF1'] },
  { type: 'match', ids: ['SF2'] },
  { type: 'divider', label: 'SUNDAY' },
  { type: 'break', label: 'BREAKFAST', time: '08:00\u201309:30', icon: 'coffee' },
  { type: 'match', ids: ['3RD'] },
  { type: 'match', ids: ['FIN'] },
];

export const PITCH_5: PitchConfig = {
  id: 5,
  name: '10 Teams (Groups + Playoffs)',
  description: '10 teams \u2022 Groups into Single Elim \u2022 Friday\u2013Sunday',
  teamCount: 10,
  format: 'Groups + Single Elimination',
  matches: PITCH_5_MATCHES,
  winnerConnections: [
    { from: 'QF', to: 'SF1' },
  ],
  loserConnections: [],
  fridayBo1: PITCH_5_FRIDAY_BO1,
  fridayBo3: PITCH_5_FRIDAY_BO3,
  bracketSchedule: PITCH_5_BRACKET_SCHEDULE,
  hasGroupToggle: true,
  groups: [
    { name: 'Group A (5 teams)', seeds: [
      { seed: 1, dest: '\u2192 Bracket (seeded by overall record)', color: 'upper' },
      { seed: 2, dest: '\u2192 Bracket (seeded by overall record)', color: 'upper' },
      { seed: 3, dest: '\u2192 Best 3rd place may advance', color: 'upper' },
      { seed: 4, dest: '\u2192 Eliminated after day 1', color: 'lower' },
      { seed: 5, dest: '\u2192 Eliminated after day 1', color: 'lower' },
    ]},
    { name: 'Group B (5 teams)', seeds: [
      { seed: 1, dest: '\u2192 Bracket (seeded by overall record)', color: 'upper' },
      { seed: 2, dest: '\u2192 Bracket (seeded by overall record)', color: 'upper' },
      { seed: 3, dest: '\u2192 Best 3rd place may advance', color: 'upper' },
      { seed: 4, dest: '\u2192 Eliminated after day 1', color: 'lower' },
      { seed: 5, dest: '\u2192 Eliminated after day 1', color: 'lower' },
    ]},
  ],
  seedingDescription: '2 groups of 5. Full round-robin BO1 on Friday (5 rounds). Top 2 per group + best 3rd place = 5 advance. Bottom 5 eliminated after day 1. #1 seed gets bye to semifinals.',
  bracketPositions: [
    { id: 'QF', top: 90, left: 20, section: 'UPPER' },
    { id: 'SF1', top: 90, left: 300, section: 'UPPER' },
    { id: 'SF2', top: 310, left: 300, section: 'UPPER' },
    { id: 'FIN', top: 190, left: 580, section: 'UPPER' },
    { id: '3RD', top: 420, left: 580, section: 'UPPER' },
  ],
  bracketConnectors: [
    // QF → SF1
    { start: { x: 200, y: 162 }, end: { x: 300, y: 162 } },
    // SF1 → FIN
    { start: { x: 480, y: 162 }, end: { x: 580, y: 262 } },
    // SF2 → FIN
    { start: { x: 480, y: 382 }, end: { x: 580, y: 262 } },
  ],
  bracketHeight: 560,
  bracketRoundHeaders: [
    { title: 'Quarterfinal', top: 55, left: 60 },
    { title: 'Semifinals', top: 55, left: 340 },
    { title: 'Grand Final', top: 155, left: 620 },
    { title: '3rd Place', top: 385, left: 620 },
  ],
  bracketByeLabels: [
    { title: '#1 Seed BYE \u2192', top: 125, left: 230 },
  ],
  showUpperLowerLabels: false,
  mobileSections: [
    { title: 'GROUPS (FRIDAY)', sectionClass: 'upper', rounds: [
      { label: 'Round 1 (4 matches)', ids: ['GR1'] },
      { label: 'Round 2 (4 matches)', ids: ['GR2'] },
      { label: 'Round 3 (4 matches)', ids: ['GR3'] },
      { label: 'Round 4 (4 matches)', ids: ['GR4'] },
      { label: 'Round 5 (4 matches)', ids: ['GR5'] },
    ]},
    { title: 'BRACKET (SAT\u2013SUN)', sectionClass: 'finals', rounds: [
      { label: 'Quarterfinal', ids: ['QF'] },
      { label: 'Semifinals', ids: ['SF1', 'SF2'] },
      { label: '3rd Place', ids: ['3RD'] },
      { label: 'Grand Final', ids: ['FIN'] },
    ]},
  ],
  flowDiagram: [
    { type: 'label', text: 'Groups (Friday):', section: 'upper' },
    { type: 'line', text: '  2 groups of 5 \u2014 round-robin BO1 (5 rounds)' },
    { type: 'line', text: '  Top 2 per group + best 3rd \u2192 5 advance' },
    { type: 'line', text: '  Bottom 5 eliminated after day 1' },
    { type: 'label', text: 'Bracket (Sat\u2013Sun):', section: 'upper' },
    { type: 'line', text: '  QF: #4 vs #5 \u2500\u2500\u2192 SF1 (vs #1 seed) \u2500\u2500\u2510' },
    { type: 'line', text: '                     SF2: #2 vs #3    \u2500\u2500\u2524\u2500\u2500\u2192 Grand Final' },
    { type: 'note', text: '  \u21B3 #1 seed gets bye to semifinals' },
    { type: 'note', text: '  \u21B3 SF losers play for 3rd place' },
  ],
};

// ============================================================
// Pitch 6 — 10 Teams: Groups + Double Elim (8 advance)
// ============================================================

const PITCH_6_MATCHES: PitchMatch[] = [
  // Group Stage — Friday (BO1, 5 rounds for full round-robin in groups of 5)
  { id: 'GR1', label: 'Round 1', bo: 'BO1', time: '17:00', day: 'Friday', section: 'upper', round: -1, slot: 0, team1: '4 group matches', team2: '' },
  { id: 'GR2', label: 'Round 2', bo: 'BO1', time: '18:00', day: 'Friday', section: 'upper', round: -1, slot: 1, team1: '4 group matches', team2: '' },
  { id: 'GR3', label: 'Round 3', bo: 'BO1', time: '19:00', day: 'Friday', section: 'upper', round: -1, slot: 2, team1: '4 group matches', team2: '' },
  { id: 'GR4', label: 'Round 4', bo: 'BO1', time: '20:00', day: 'Friday', section: 'upper', round: -1, slot: 3, team1: '4 group matches', team2: '' },
  { id: 'GR5', label: 'Round 5', bo: 'BO1', time: '21:00', day: 'Friday', section: 'upper', round: -1, slot: 4, team1: '4 group matches', team2: '' },
  // Group Stage — Friday (BO3, 5 rounds — long day)
  { id: 'GR1B', label: 'Round 1', bo: 'BO3', time: '12:00', day: 'Friday', section: 'upper', round: -1, slot: 0, team1: '4 group matches', team2: '' },
  { id: 'GR2B', label: 'Round 2', bo: 'BO3', time: '14:00', day: 'Friday', section: 'upper', round: -1, slot: 1, team1: '4 group matches', team2: '' },
  { id: 'GR3B', label: 'Round 3', bo: 'BO3', time: '16:00', day: 'Friday', section: 'upper', round: -1, slot: 2, team1: '4 group matches', team2: '' },
  { id: 'GR4B', label: 'Round 4', bo: 'BO3', time: '19:00', day: 'Friday', section: 'upper', round: -1, slot: 3, team1: '4 group matches', team2: '' },
  { id: 'GR5B', label: 'Round 5', bo: 'BO3', time: '21:00', day: 'Friday', section: 'upper', round: -1, slot: 4, team1: '4 group matches', team2: '' },
  // Upper Bracket R1 — Seed 2 vs Seed 3 cross-group (same as Pitch 1)
  { id: 'K1', label: 'Match 1', bo: 'BO1', time: '11:00', day: 'Saturday', section: 'upper', round: 0, slot: 0, team1: 'G1 #2', team2: 'G2 #3' },
  { id: 'K2', label: 'Match 2', bo: 'BO1', time: '11:00', day: 'Saturday', section: 'upper', round: 0, slot: 1, team1: 'G2 #2', team2: 'G1 #3' },
  // Upper Bracket R2 — #1 seeds waiting for winners
  { id: 'UB1', label: 'UB 1', bo: 'BO3', time: '12:00', day: 'Saturday', section: 'upper', round: 1, slot: 0, team1: 'G2 #1', team2: 'Winner Match 1' },
  { id: 'UB2', label: 'UB 2', bo: 'BO3', time: '12:00', day: 'Saturday', section: 'upper', round: 1, slot: 1, team1: 'G1 #1', team2: 'Winner Match 2' },
  // Upper Bracket Final
  { id: 'UBF', label: 'UB Final', bo: 'BO3', time: '21:00', day: 'Saturday', section: 'upper', round: 2, slot: 0, team1: 'Winner UB 1', team2: 'Winner UB 2' },
  // Grand Final
  { id: 'GF', label: 'Grand Final', bo: 'BO3', time: '13:00', day: 'Sunday', section: 'final', round: 3, slot: 0, team1: 'Winner UB Final', team2: 'Winner LB Final' },
  // Lower Bracket R1 — #4 seeds vs losers of BO1s
  { id: 'LB1', label: 'LB 1', bo: 'BO3', time: '15:00', day: 'Saturday', section: 'lower', round: 0, slot: 0, team1: 'G2 #4', team2: 'Loser Match 1' },
  { id: 'LB2', label: 'LB 2', bo: 'BO3', time: '15:00', day: 'Saturday', section: 'lower', round: 0, slot: 1, team1: 'G1 #4', team2: 'Loser Match 2' },
  // Lower Bracket R2
  { id: 'LB3', label: 'LB 3', bo: 'BO3', time: '18:00', day: 'Saturday', section: 'lower', round: 1, slot: 0, team1: 'Winner LB 1', team2: 'Loser UB 1' },
  { id: 'LB4', label: 'LB 4', bo: 'BO3', time: '18:00', day: 'Saturday', section: 'lower', round: 1, slot: 1, team1: 'Winner LB 2', team2: 'Loser UB 2' },
  // Lower Bracket Semi
  { id: 'LB5', label: 'LB 5', bo: 'BO3', time: '21:00', day: 'Saturday', section: 'lower', round: 2, slot: 0, team1: 'Winner LB 3', team2: 'Winner LB 4' },
  // Lower Bracket Final
  { id: 'LBF', label: 'LB Final', bo: 'BO3', time: '10:00', day: 'Sunday', section: 'lower', round: 3, slot: 0, team1: 'Winner LB 5', team2: 'Loser UB Final' },
];

const PITCH_6_FRIDAY_BO1: ScheduleItem[] = [
  { type: 'break', label: 'MEDIA DAY', time: '14:00', icon: 'coffee' },
  { type: 'break', label: 'DINNER', time: '16:00\u201317:00', icon: 'utensils' },
  { type: 'match', ids: ['GR1'] },
  { type: 'match', ids: ['GR2'] },
  { type: 'match', ids: ['GR3'] },
  { type: 'match', ids: ['GR4'] },
  { type: 'match', ids: ['GR5'] },
];

const PITCH_6_FRIDAY_BO3: ScheduleItem[] = [
  { type: 'break', label: 'MEDIA DAY', time: '10:00', icon: 'coffee' },
  { type: 'match', ids: ['GR1B'] },
  { type: 'match', ids: ['GR2B'] },
  { type: 'match', ids: ['GR3B'] },
  { type: 'break', label: 'DINNER', time: '18:00\u201318:45', icon: 'utensils' },
  { type: 'match', ids: ['GR4B'] },
  { type: 'match', ids: ['GR5B'] },
];

const PITCH_6_BRACKET_SCHEDULE: ScheduleItem[] = [
  { type: 'divider', label: 'SATURDAY' },
  { type: 'break', label: 'BREAKFAST', time: '08:00', icon: 'coffee' },
  { type: 'match', ids: ['K1', 'K2'] },
  { type: 'match', ids: ['UB1', 'UB2'] },
  { type: 'break', label: 'LUNCH', time: '14:00\u201315:00', icon: 'utensils' },
  { type: 'match', ids: ['LB1', 'LB2'] },
  { type: 'match', ids: ['LB3', 'LB4'] },
  { type: 'break', label: 'DINNER', time: '20:00\u201320:30', icon: 'bowl' },
  { type: 'match', ids: ['LB5', 'UBF'] },
  { type: 'divider', label: 'SUNDAY' },
  { type: 'break', label: 'BREAKFAST', time: '08:00\u201309:30', icon: 'coffee' },
  { type: 'match', ids: ['LBF'] },
  { type: 'match', ids: ['GF'] },
];

export const PITCH_6: PitchConfig = {
  id: 6,
  name: '10 Teams (Groups + DE)',
  description: '10 teams \u2022 Double Elimination \u2022 Friday\u2013Sunday',
  teamCount: 10,
  format: 'Groups + Double Elimination',
  matches: PITCH_6_MATCHES,
  winnerConnections: [
    { from: 'K1', to: 'UB1' },
    { from: 'K2', to: 'UB2' },
    { from: 'UB1', to: 'UBF' },
    { from: 'UB2', to: 'UBF' },
    { from: 'UBF', to: 'GF' },
    { from: 'LB1', to: 'LB3' },
    { from: 'LB2', to: 'LB4' },
    { from: 'LB3', to: 'LB5' },
    { from: 'LB4', to: 'LB5' },
    { from: 'LB5', to: 'LBF' },
    { from: 'LBF', to: 'GF' },
  ],
  loserConnections: [
    { from: 'K1', to: 'LB1' },
    { from: 'K2', to: 'LB2' },
    { from: 'UB1', to: 'LB3' },
    { from: 'UB2', to: 'LB4' },
    { from: 'UBF', to: 'LBF' },
  ],
  fridayBo1: PITCH_6_FRIDAY_BO1,
  fridayBo3: PITCH_6_FRIDAY_BO3,
  bracketSchedule: PITCH_6_BRACKET_SCHEDULE,
  hasGroupToggle: true,
  groups: [
    { name: 'Group 1 (5 teams)', seeds: [
      { seed: 1, dest: '\u2192 UB 2 (bye, awaits Match 2 winner)', color: 'upper', isBye: true },
      { seed: 2, dest: '\u2192 Match 1 vs G2 #3 (BO1)', color: 'upper' },
      { seed: 3, dest: '\u2192 Match 2 vs G2 #2 (BO1)', color: 'upper' },
      { seed: 4, dest: '\u2192 LB 2 vs Loser Match 2 (BO3)', color: 'lower' },
      { seed: 5, dest: '\u2192 Eliminated after day 1', color: 'lower' },
    ]},
    { name: 'Group 2 (5 teams)', seeds: [
      { seed: 1, dest: '\u2192 UB 1 (bye, awaits Match 1 winner)', color: 'upper', isBye: true },
      { seed: 2, dest: '\u2192 Match 2 vs G1 #3 (BO1)', color: 'upper' },
      { seed: 3, dest: '\u2192 Match 1 vs G1 #2 (BO1)', color: 'upper' },
      { seed: 4, dest: '\u2192 LB 1 vs Loser Match 1 (BO3)', color: 'lower' },
      { seed: 5, dest: '\u2192 Eliminated after day 1', color: 'lower' },
    ]},
  ],
  seedingDescription: '2 groups of 5. Full round-robin on Friday (5 rounds). #5 seeds eliminated. Top 4 per group enter double elimination bracket \u2014 same structure as Pitch 1 with 12 bracket matches.',
  // Same bracket layout as Pitch 1
  bracketPositions: [
    { id: 'K1', top: 90, left: 20, section: 'UPPER' },
    { id: 'K2', top: 370, left: 20, section: 'UPPER' },
    { id: 'UB1', top: 90, left: 240, section: 'UPPER' },
    { id: 'UB2', top: 370, left: 240, section: 'UPPER' },
    { id: 'UBF', top: 220, left: 460, section: 'UPPER' },
    { id: 'GF', top: 220, left: 680, section: 'UPPER' },
    { id: 'LB1', top: 660, left: 20, section: 'LOWER' },
    { id: 'LB2', top: 800, left: 20, section: 'LOWER' },
    { id: 'LB3', top: 660, left: 240, section: 'LOWER' },
    { id: 'LB4', top: 800, left: 240, section: 'LOWER' },
    { id: 'LB5', top: 730, left: 460, section: 'LOWER' },
    { id: 'LBF', top: 730, left: 680, section: 'LOWER' },
  ],
  bracketConnectors: [
    { start: { x: 200, y: 162 }, end: { x: 240, y: 162 } },
    { start: { x: 200, y: 442 }, end: { x: 240, y: 442 } },
    { start: { x: 420, y: 162 }, end: { x: 460, y: 292 } },
    { start: { x: 420, y: 442 }, end: { x: 460, y: 292 } },
    { start: { x: 640, y: 292 }, end: { x: 680, y: 292 } },
    { start: { x: 200, y: 732 }, end: { x: 240, y: 732 } },
    { start: { x: 200, y: 872 }, end: { x: 240, y: 872 } },
    { start: { x: 420, y: 732 }, end: { x: 460, y: 802 } },
    { start: { x: 420, y: 872 }, end: { x: 460, y: 802 } },
    { start: { x: 640, y: 802 }, end: { x: 680, y: 802 } },
  ],
  bracketHeight: 945,
  bracketRoundHeaders: [
    { title: 'Upper Final', top: 180, left: 500 },
    { title: 'Grand Final', top: 180, left: 720 },
    { title: 'Lower Semi', top: 690, left: 490 },
    { title: 'Lower Final', top: 690, left: 710 },
  ],
  bracketByeLabels: [
    { title: 'G2 #1 BYE \u2192', top: 155, left: 190 },
    { title: 'G1 #1 BYE \u2192', top: 435, left: 190 },
  ],
  bracketSectionDividerY: 590,
  showUpperLowerLabels: true,
  mobileSections: [
    { title: 'UPPER BRACKET', sectionClass: 'upper', rounds: [
      { label: 'Round 1 (BO1)', ids: ['K1', 'K2'] },
      { label: 'Round 2 (BO3)', ids: ['UB1', 'UB2'] },
      { label: 'Upper Final', ids: ['UBF'] },
    ]},
    { title: 'LOWER BRACKET', sectionClass: 'lower', rounds: [
      { label: 'Round 1', ids: ['LB1', 'LB2'] },
      { label: 'Round 2', ids: ['LB3', 'LB4'] },
      { label: 'Lower Semi', ids: ['LB5'] },
      { label: 'Lower Final', ids: ['LBF'] },
    ]},
    { title: 'GRAND FINAL', sectionClass: 'finals', rounds: [
      { label: '', ids: ['GF'] },
    ]},
  ],
  flowDiagram: [
    { type: 'label', text: 'Groups (Friday):', section: 'upper' },
    { type: 'line', text: '  2 groups of 5 \u2014 round-robin (5 rounds)' },
    { type: 'line', text: '  #5 seeds eliminated, top 4 per group advance' },
    { type: 'label', text: 'Upper Bracket:', section: 'upper' },
    { type: 'line', text: '  Match 1 (G1#2 vs G2#3) \u2500\u2500\u2192 UB 1 (vs G2#1) \u2500\u2500\u2510' },
    { type: 'line', text: '  Match 2 (G2#2 vs G1#3) \u2500\u2500\u2192 UB 2 (vs G1#1) \u2500\u2500\u2524\u2500\u2500\u2192 UB Final \u2500\u2500\u2192 GRAND FINAL' },
    { type: 'label', text: 'Lower Bracket:', section: 'lower' },
    { type: 'line', text: '  LB 1 (G2#4 vs Loser M1) \u2500\u2500\u2192 LB 3 (vs Loser UB1) \u2500\u2500\u2510' },
    { type: 'line', text: '  LB 2 (G1#4 vs Loser M2) \u2500\u2500\u2192 LB 4 (vs Loser UB2) \u2500\u2500\u2524\u2500\u2500\u2192 LB 5 \u2500\u2500\u2192 LB Final \u2500\u2500\u2192 GRAND FINAL' },
    { type: 'note', text: '  \u21B3 Same bracket as Pitch 1 \u2014 12 matches, both streams active' },
  ],
};

// ============================================================
// All Pitches
// ============================================================
export const ALL_PITCHES: PitchConfig[] = [PITCH_1, PITCH_2, PITCH_3, PITCH_4, PITCH_5, PITCH_6];
